import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useError } from '../../../context/ErrorContext';
import Layout from '../../../components/layout/Layout';
import PaymentMethodsSelector from '../components/PaymentMethodsSelector';
import { usePaymentMethods } from '../hooks/usePaymentMethods';
import ManualPaymentForm, { type ManualPaymentSubmitData } from '../components/ManualPaymentForm';
import PaymentController from '../services/PaymentController';
import MpPayerEmailField from '../components/MpPayerEmailField';
import { billingApi, planNameByMonths, perPeriodOf, type OwnerAccount, type PlanOption, type AutopayStatus } from '../../../api/billingApi';
import { branchTotalPrice, branchBreakdown } from '../../../utils/branchPricing';
import { ArrowLeft, CreditCard, Check, AlertCircle, RefreshCw } from 'lucide-react';

// Motivo por el que un periodo no se puede elegir AHORA (lo decide el servidor, aquí solo texto).
const reasonText = (o: PlanOption): string =>
  (o.reason === 'PLAN_LOCKED' ? 'Precio acordado: para cambiar de periodo escríbenos'
    : o.reason === 'AT_RENEWAL' ? 'Disponible cuando falten 30 días o menos para renovar' : '');

const PaymentPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { addError, handleFirebaseError, clearErrors } = useError();
  const navigate = useNavigate();
  const location = useLocation();

  // Plan base (de la navegación o PRO anual por defecto). Es solo el periodo PRESELECCIONADO:
  // desde el 27-sep-2026 el dueño elige aquí mensual / semestral / anual.
  const basePlan = location.state?.plan || PaymentController.PLANS.PRO_YEARLY;
  const maxBranches = Math.max(1, currentUser?.maxBranches ?? 1);
  // 🔑 EL MONTO Y LOS PERIODOS LOS MANDA EL SERVIDOR (13-ago / 27-sep-2026). Antes se recalculaba
  // aquí con la tarifa escalonada, y a los clientes con precio ACORDADO esta pantalla les enseñaba
  // $2,500 mientras el cobro se generaba por $5,000 / $6,000 / $500 al mes. El servidor resuelve
  // el monto de CADA periodo en un solo sitio (lib/pricing.planOptions); aquí solo se muestra.
  // Ya no hay cálculo local de respaldo: si el servidor no contesta, no se enseña un número.
  const [cuenta, setCuenta] = useState<{ price: number; planMonths: number; priceIsCustom: boolean;
    planOptions: PlanOption[]; autopayStatus: AutopayStatus | null; isTrial: boolean;
    planCode: string; planVigente: boolean } | null>(null);
  const [planId, setPlanId] = useState<string>(basePlan.id);
  useEffect(() => {
    let vivo = true;
    billingApi.getAccount()
      .then((c: OwnerAccount) => {
        if (!vivo) return;
        setCuenta({ price: c.price, planMonths: c.planMonths, priceIsCustom: c.priceIsCustom,
          planOptions: c.planOptions || [], autopayStatus: c.autopayStatus, isTrial: c.isTrial,
          planCode: c.planCode, planVigente: c.planVigente });
        // Periodo inicial: el de la navegación si está permitido; si no, el vigente; si no, el primero permitido.
        const allowed = (c.planOptions || []).filter((o) => o.allowed);
        const pick = allowed.find((o) => o.code === basePlan.id) || allowed.find((o) => o.current) || allowed[0];
        if (pick) setPlanId(pick.code);
        // Embudo de pago (11-oct-2026): llegó a la página de cobro, con el periodo con que arranca.
        window.gtag?.('event', 'payment_view', { plan: pick ? pick.code : basePlan.id });
      })
      .catch(() => { /* sin cuenta no se enseña monto; los botones quedan deshabilitados */ });
    return () => { vivo = false; };
  }, [basePlan.id]);
  const opcion = cuenta ? (cuenta.planOptions.find((o) => o.code === planId) || null) : null;
  const montoListo = !!opcion;
  // Candado de domiciliación (23-oct-2026): con plan vigente solo se domicilia ese mismo periodo.
  const autopayOtroPeriodo = !!cuenta && cuenta.planVigente && planId !== cuenta.planCode;
  const escalonadoAmount = opcion ? opcion.amount : 0;
  const nombrePlanPorMeses = opcion ? planNameByMonths(opcion.months) : null;
  // El desglose por tramos solo cuadra con la tarifa estándar ANUAL de lealtad (con precio
  // acordado, plan mensual/semestral o tarifa plana dental/POS no suma el total): se enseña
  // únicamente cuando el monto del servidor ES la suma de los tramos.
  const branchTiers = (planId === 'pro_yearly' && maxBranches > 1 && opcion && cuenta && !cuenta.priceIsCustom
    && opcion.amount === branchTotalPrice(maxBranches)) ? branchBreakdown(maxBranches) : [];
  const plan = { ...PaymentController.planById(planId), amount: escalonadoAmount,
    name: nombrePlanPorMeses || basePlan.name };
  const montoTxt = montoListo ? `${plan.currency} $${plan.amount.toLocaleString('es-MX')}` : '…';

  const { methods } = usePaymentMethods();
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [paymentSuccess, setPaymentSuccess] = useState<boolean>(false);
  const [paymentId, setPaymentId] = useState<string | null>(null);

  // Reset state when component mounts
  useEffect(() => {
    if (clearErrors) clearErrors();
    setPaymentSuccess(false);
    setPaymentId(null);
  }, [clearErrors]);

  // Selecciona por defecto el primer método disponible una vez cargada la config
  useEffect(() => {
    if (!selectedMethod) {
      const firstAvailable = methods.find((m) => m.available);
      if (firstAvailable) setSelectedMethod(firstAvailable.id);
    }
  }, [methods, selectedMethod]);

  // Único llamador: ManualPaymentForm, que emite un OBJETO plano (monto, correo, whatsapp,
  // url del comprobante) — nunca un FormData, que es lo que decía la firma. El runtime
  // siempre estuvo bien: PaymentController.processPayment lo trata como objeto.
  const handlePayment = async (formData: ManualPaymentSubmitData) => {
    if (!currentUser) {
      if (addError) addError('Debes iniciar sesión para realizar el pago', 'auth', 'payment-auth');
      return;
    }

    try {
      setSubmitting(true);
      if (clearErrors) clearErrors();
      
      // Process payment
      window.gtag?.('event', 'checkout_start', { method: selectedMethod, plan: planId });
      const result = await PaymentController.processPayment(selectedMethod as string, currentUser, { ...formData, planId });
      
      // Handle success
      setPaymentId(result.id);
      setPaymentSuccess(true);
      
      // If there's a redirect URL, navigate to it
      if (result.redirectUrl) {
        window.location.href = result.redirectUrl;
        return;
      }

    } catch (error) {
      console.error('Error processing payment:', error);
      if (handleFirebaseError) handleFirebaseError(error, 'payment-process');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBtcpayPayment = async () => {
    if (!currentUser) {
      if (addError) addError('Debes iniciar sesión para realizar el pago', 'auth', 'payment-auth');
      return;
    }
    try {
      setSubmitting(true);
      if (clearErrors) clearErrors();
      window.gtag?.('event', 'checkout_start', { method: 'btcpay', plan: plan.id });
      const result = await PaymentController.processPayment('btcpay', currentUser, { planId: plan.id });
      if (result.redirectUrl) {
        window.location.href = result.redirectUrl;
        return;
      }
    } catch (error) {
      console.error('Error processing BTCPay payment:', error);
      if (handleFirebaseError) handleFirebaseError(error, 'payment-process');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMercadoPagoPayment = async () => {
    if (!currentUser) {
      if (addError) addError('Debes iniciar sesión para realizar el pago', 'auth', 'payment-auth');
      return;
    }
    try {
      setSubmitting(true);
      if (clearErrors) clearErrors();
      window.gtag?.('event', 'checkout_start', { method: 'mercadopago', plan: plan.id });
      const result = await PaymentController.processPayment('mercadopago', currentUser, { planId: plan.id });
      if (result.redirectUrl) {
        window.location.href = result.redirectUrl;
        return;
      }
    } catch (error) {
      console.error('Error processing Mercado Pago payment:', error);
      if (handleFirebaseError) handleFirebaseError(error, 'payment-process');
    } finally {
      setSubmitting(false);
    }
  };

  // DOMICILIACIÓN desde aquí (27-sep-2026): autoriza la tarjeta una vez y el periodo elegido se
  // cobra solo cada ciclo. Desde gratis el primer cargo es inmediato; con plan vigente, al vencer.
  const [autopayBusy, setAutopayBusy] = useState(false);
  const [autopayAskEmail, setAutopayAskEmail] = useState(false);   // paso «correo de MP» antes de ir a MP
  const handleAutopay = async (payerEmail: string) => {
    if (!currentUser) return;
    try {
      setAutopayBusy(true);
      if (clearErrors) clearErrors();
      window.gtag?.('event', 'checkout_start', { method: 'autopay', plan: planId });
      const r = await billingApi.startAutopay(planId, payerEmail);
      window.location.href = r.checkoutUrl;
    } catch (error) {
      console.error('Error iniciando domiciliación:', error);
      if (handleFirebaseError) handleFirebaseError(error, 'payment-process');
      setAutopayBusy(false);
    }
  };

  const handleGoBack = () => {
    if (clearErrors) clearErrors();
    navigate('/dashboard/subscription');
  };

  // Render Success View
  const renderSuccessView = () => (
    <Layout>
      <div className="max-w-3xl mx-auto px-4">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fade-in-up" style={{animationDelay: '0.1s'}}>
          <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
            <CreditCard className="h-6 w-6 mr-2 text-green-500" />
            Pago Recibido
          </h1>

          <button
            onClick={handleGoBack}
            className="inline-flex items-center px-3 py-2 border border-line shadow-sm text-sm rounded-md text-content-2 bg-surface hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver a Suscripción
          </button>
        </div>

        <div className="bg-surface shadow overflow-hidden sm:rounded-lg animate-fade-in-up" style={{animationDelay: '0.15s'}}>
          <div className="px-4 py-5 sm:p-6 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-500/20 mb-4 animate-pop">
              <Check className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-lg leading-6 font-medium text-content mb-2">
              Comprobante Recibido Correctamente
            </h2>
            <p className="text-sm text-content-3 mb-4">
              ¡Gracias por tu pago! Tu comprobante ha sido recibido y está siendo procesado.
            </p>
            
            <div className="bg-yellow-50 dark:bg-yellow-500/10 border-l-4 border-yellow-400 p-4 mb-4 text-left animate-fade-in" style={{animationDelay: '0.15s'}}>
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    <strong>Importante:</strong> Tu suscripción PRO será activada en un plazo de 24 horas hábiles, una vez que hayamos verificado tu pago. Recibirás una notificación cuando tu suscripción esté activa.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-md bg-surface-2 p-4 text-left animate-fade-in" style={{animationDelay: '0.15s'}}>
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-content-2">
                    <span className="font-medium">ID de Pago:</span> {paymentId}
                  </p>
                  <p className="text-sm text-content-2 mt-1">
                    <span className="font-medium">Plan:</span> {plan.name}
                  </p>
                  <p className="text-sm text-content-2 mt-1">
                    <span className="font-medium">Monto:</span> {plan.currency} ${plan.amount.toLocaleString('es-MX')}
                  </p>
                  <p className="text-sm text-content-2 mt-1">
                    <span className="font-medium">Estado:</span> <span className="text-yellow-600 dark:text-yellow-400">Pendiente de verificación</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );

  // If payment succeeded, show success message with instructions
  if (paymentSuccess) {
    return renderSuccessView();
  }

  // Render payment form
  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fade-in-up" style={{animationDelay: '0.1s'}}>
          <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
            <CreditCard className="h-6 w-6 mr-2 text-accent" />
            Proceso de Pago
          </h1>

          <button
            onClick={handleGoBack}
            className="inline-flex items-center px-3 py-2 border border-line shadow-sm text-sm rounded-md text-content-2 bg-surface hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Cancelar
          </button>
        </div>

        <div className="bg-surface shadow overflow-hidden sm:rounded-lg animate-fade-in-up" style={{animationDelay: '0.15s'}}>
          <div className="p-4 sm:p-6">
            {/* Periodo (27-sep-2026): el dueño elige mensual / semestral / anual. Los montos y
                qué se puede elegir ahora vienen del servidor (precio acordado = solo su plan;
                acortar periodo = solo a ≤30 días de renovar, porque los periodos se SUMAN). */}
            {cuenta && cuenta.planOptions.length > 0 && (
              <div className="mb-6 animate-fade-in-up" style={{animationDelay: '0.12s'}}>
                <h2 className="text-lg font-medium text-content mb-2">Elige tu periodo</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {cuenta.planOptions.map((o) => {
                    const sel = o.code === planId;
                    return (
                      <button key={o.code} type="button" disabled={!o.allowed} onClick={() => setPlanId(o.code)}
                        title={o.allowed ? '' : reasonText(o)}
                        className={'text-left rounded-lg border px-4 py-3 transition-colors ' +
                          (sel ? 'border-accent bg-accent/10 ring-1 ring-accent' : 'border-line bg-surface hover:bg-surface-2') +
                          (o.allowed ? '' : ' opacity-50 cursor-not-allowed')}>
                        <span className="block text-sm font-semibold text-content">{o.label}{o.current ? ' · tu plan actual' : ''}</span>
                        <span className="block text-xl font-bold text-content">${o.amount.toLocaleString('es-MX')}</span>
                        <span className="block text-xs text-content-3">{o.currency} {perPeriodOf(o.months)}</span>
                        {!o.allowed && <span className="block text-[11px] text-content-3 mt-1">{reasonText(o)}</span>}
                      </button>
                    );
                  })}
                </div>
                {planId === 'pro_yearly' && cuenta.planOptions.some((o) => o.code === 'pro_monthly' && o.allowed) && (() => {
                  const m = cuenta.planOptions.find((o) => o.code === 'pro_monthly');
                  const y = cuenta.planOptions.find((o) => o.code === 'pro_yearly');
                  return m && y && m.amount * 12 > y.amount
                    ? <p className="mt-2 text-xs text-content-3">Con el anual ahorras ${(m.amount * 12 - y.amount).toLocaleString('es-MX')} frente a pagar mes a mes.</p>
                    : null;
                })()}
              </div>
            )}

            {/* Order Summary */}
            <div className="mb-6 pb-6 border-b border-line animate-fade-in-up" style={{animationDelay: '0.15s'}}>
              <h2 className="text-lg font-medium text-content mb-2">Resumen de la Orden</h2>
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-base font-medium text-content">{plan.name}</p>
                  <p className="text-sm text-content-3">{plan.description}</p>
                  {branchTiers.length > 0 && (
                    <p className="text-xs text-content-3 mt-1">
                      {maxBranches} sucursales (precio escalonado):{' '}
                      {branchTiers.map((t, i) => (
                        <span key={i}>{i > 0 ? ' + ' : ''}{t.count} × ${t.price.toLocaleString('es-MX')}</span>
                      ))}
                    </p>
                  )}
                </div>
                <div className="text-xl font-semibold">{montoTxt}</div>
              </div>
            </div>
            
            {/* Payment Methods */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-1 animate-fade-in-up" style={{animationDelay: '0.15s'}}>
                <PaymentMethodsSelector
                  onSelectMethod={setSelectedMethod}
                  selectedMethod={selectedMethod}
                  methods={methods}
                />
              </div>
              
              <div className="md:col-span-2 animate-fade-in-up" style={{animationDelay: '0.15s'}}>
                {selectedMethod === 'manual' && montoListo && (
                  <ManualPaymentForm
                    onSubmit={handlePayment}
                    isSubmitting={submitting}
                    amount={plan.amount}
                    currency={plan.currency}
                  />
                )}

                {selectedMethod === 'btcpay' && (
                  <div className="flex flex-col h-full">
                    <h3 className="text-base font-medium text-content mb-2">Pago con criptomonedas</h3>
                    <p className="text-sm text-content-3 mb-4">
                      Serás redirigido a una página de pago segura de BTCPay donde podrás pagar
                      con Bitcoin, Litecoin o Lightning Network. El importe en cripto se calcula
                      automáticamente a partir de {montoTxt}.
                    </p>
                    <div className="bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 rounded-lg p-4 mb-6 text-sm text-blue-800 dark:text-blue-300">
                      Tu suscripción PRO se activará <strong>automáticamente</strong> en cuanto el pago se
                      confirme en la red (normalmente unos minutos). No necesitas enviar comprobante.
                    </div>
                    <button
                      type="button"
                      onClick={handleBtcpayPayment}
                      disabled={submitting || !montoListo}
                      className="inline-flex items-center justify-center px-4 py-2.5 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
                    >
                      {submitting ? 'Generando cobro…' : `Continuar al pago — ${montoTxt}`}
                    </button>
                  </div>
                )}

                {selectedMethod === 'mercadopago' && (
                  <div className="flex flex-col h-full">
                    <h3 className="text-base font-medium text-content mb-2">Pago con Mercado Pago</h3>
                    <p className="text-sm text-content-3 mb-4">
                      Serás redirigido a la página segura de Mercado Pago donde podrás pagar con
                      tarjeta de débito/crédito, en efectivo en OXXO o por transferencia (SPEI),
                      por {montoTxt}.
                    </p>
                    <div className="bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 rounded-lg p-4 mb-6 text-sm text-blue-800 dark:text-blue-300">
                      Tu suscripción PRO se activará <strong>automáticamente</strong> en cuanto se
                      confirme el pago. Con tarjeta es inmediato; en OXXO/SPEI puede tardar unas horas.
                    </div>
                    <button
                      type="button"
                      onClick={handleMercadoPagoPayment}
                      disabled={submitting || !montoListo}
                      className="inline-flex items-center justify-center px-4 py-2.5 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
                    >
                      {submitting ? 'Generando cobro…' : `Pagar una vez — ${montoTxt}`}
                    </button>
                    {/* Pago automático (domiciliación, 27-sep-2026): misma tarjeta, se cobra solo cada periodo. */}
                    {cuenta && cuenta.autopayStatus !== 'authorized' && !cuenta.isTrial && opcion && (
                      <div className="mt-4 rounded-lg border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 p-4">
                        <p className="text-sm text-emerald-900 dark:text-emerald-200">
                          <strong>O deja que se cobre solo:</strong> autorizas tu tarjeta una vez y {montoTxt} {perPeriodOf(opcion.months)} se cobra
                          automáticamente cada periodo. Sin recordatorios ni comprobantes; lo apagas cuando quieras desde Suscripción.
                        </p>
                        {autopayOtroPeriodo ? (
                          <p data-testid="autopay-otro-periodo" className="mt-3 text-sm text-emerald-900/80 dark:text-emerald-200/80">
                            Para domiciliar otro periodo, primero págalo una vez (tarjeta o transferencia); la domiciliación se activa con el plan que tengas vigente.
                          </p>
                        ) : autopayAskEmail ? (
                          <MpPayerEmailField defaultEmail={currentUser?.email || ''} busy={autopayBusy}
                            onConfirm={handleAutopay} onCancel={() => setAutopayAskEmail(false)} />
                        ) : (
                          <button
                            type="button"
                            data-testid="btn-autopay-start"
                            onClick={() => setAutopayAskEmail(true)}
                            disabled={autopayBusy || submitting}
                            className="mt-3 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            <RefreshCw className="h-4 w-4" /> {`Activar pago automático — ${montoTxt} ${perPeriodOf(opcion.months)}`}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {selectedMethod && selectedMethod !== 'manual' && selectedMethod !== 'btcpay' && selectedMethod !== 'mercadopago' &&
                  methods.find(m => m.id === selectedMethod)?.comingSoon && (
                  <div className="flex flex-col items-center justify-center h-64">
                    <div className="bg-yellow-50 dark:bg-yellow-500/10 border-l-4 border-yellow-400 p-4 animate-fade-in" style={{animationDelay: '0.15s'}}>
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <AlertCircle className="h-5 w-5 text-yellow-400" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-300">Método en desarrollo</h3>
                          <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                            <p>
                              Este método de pago estará disponible próximamente. 
                              Por favor, selecciona otro método de pago o vuelve más tarde.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PaymentPage;